import { Columns } from "@fullscript/aviary-web";
import type { AviaryColors } from "aviary-tokens";
import type { ReactNode } from "react";

import {
  Button,
  ModalContainer,
  ModalHeader,
  Modal,
  ModalFooter,
  Typography,
  type ModalAviarySize,
} from "@aviary";
import { e2eAttributes } from "@shared/e2eAttributes";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";

import * as styles from "./ConfirmModal.styles";

interface Props {
  isOpen: boolean;
  onClose?: () => void;
  isSize?: ModalAviarySize;
  hideClose?: boolean;
  headingText: string;
  bodyText?: string;
  children?: ReactNode;
  closeButtonText?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonColor?: AviaryColors;
  secondaryButtonColor?: AviaryColors;
  primaryOnClick?: () => void;
  secondaryOnClick?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isOverflowVisible?: boolean;
}

const ConfirmModal = ({
  isOpen,
  onClose,
  isSize = "small",
  hideClose = false,
  headingText,
  bodyText = null,
  children,
  closeButtonText = "Cancel",
  primaryButtonText = "Confirm",
  secondaryButtonText,
  primaryButtonColor = "primary",
  secondaryButtonColor = "info",
  primaryOnClick,
  secondaryOnClick,
  isLoading = false,
  isDisabled,
  isOverflowVisible = false,
}: Props) => {
  const { tablet } = useBreakpoints();

  const renderCloseButton = () => {
    return (
      <>
        {!hideClose && (
          <Button
            css={styles.button}
            isOutlined
            onClick={onClose}
            disabled={isLoading}
            isFullWidth={tablet.lessThan}
            data-e2e={e2eAttributes.closeModalButton}
          >
            {closeButtonText}
          </Button>
        )}
      </>
    );
  };

  const renderPrimaryButton = () => {
    return (
      <>
        {!!primaryButtonText && primaryButtonText !== "" && primaryOnClick && (
          <Button
            isColor={primaryButtonColor}
            css={styles.button}
            onClick={primaryOnClick}
            isLoading={isLoading}
            disabled={isDisabled}
            data-e2e={e2eAttributes.confirmModalPrimaryButton}
            isFullWidth={tablet.lessThan}
          >
            {primaryButtonText}
          </Button>
        )}
      </>
    );
  };

  const renderSecondaryButton = () => {
    return (
      <>
        {!!secondaryButtonText && secondaryButtonText !== "" && secondaryOnClick && (
          <Button
            isColor={secondaryButtonColor}
            css={styles.button}
            onClick={secondaryOnClick}
            isLoading={isLoading}
            disabled={isDisabled}
            isFullWidth={tablet.lessThan}
          >
            {secondaryButtonText}
          </Button>
        )}
      </>
    );
  };
  const renderFooter = () => {
    if (tablet.lessThan) {
      return (
        <>
          {renderPrimaryButton()}
          {renderSecondaryButton()}
          {renderCloseButton()}
        </>
      );
    }

    return (
      <>
        {renderCloseButton()}
        {renderPrimaryButton()}
        {renderSecondaryButton()}
      </>
    );
  };

  const renderBody = () =>
    children ? (
      <Columns.Column>{children}</Columns.Column>
    ) : (
      <Columns.Column>
        <Typography>{bodyText}</Typography>
      </Columns.Column>
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isSize}
      hideClose={isLoading}
      disableBackdropClick={isLoading}
      isOverflowVisible={isOverflowVisible}
    >
      <ModalContainer>
        <ModalHeader>
          <Typography type="h1" sizeOverride="h4">
            {headingText}
          </Typography>
        </ModalHeader>
        <Columns>{renderBody()}</Columns>
        <ModalFooter css={styles.modalFooter}>{renderFooter()}</ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export { ConfirmModal };
